/** User-facing error copy for ZOE / operations-agent investigations. */

const GENERIC_INVESTIGATION_ERROR =
  'Something went wrong with this investigation. Please try again, or contact your ZeeFleet administrator.';

function logInvestigationError(raw: string): void {
  console.warn('[ZOE] investigation error:', raw);
}

export function formatZoeInvestigationError(err: unknown, opts: { isProfitQuestion?: boolean } = {}): string {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return 'The investigation was cancelled. You can try again with a narrower question.';
  }

  const raw = err instanceof Error ? err.message : String(err ?? 'An unexpected error occurred');
  // Scrubbed from every user-facing branch below — keep the real message in
  // the console once, here, rather than re-adding a log call per branch (and
  // risking a future branch that forgets to, same way two already had).
  logInvestigationError(raw);

  if (/failed to fetch|networkerror|could not reach the operations agent/i.test(raw)) {
    return (
      'Could not reach ZOE right now. Please try again in a moment. ' +
      'If this keeps happening, contact your ZeeFleet administrator.'
    );
  }

  if (/profit agent|profit_agent|4008|ECONNREFUSED/i.test(raw) || opts.isProfitQuestion) {
    // Timeout-shaped wording is checked before the LLM/config check below —
    // a message like "LLM request timed out" must keep its actionable
    // "try a shorter date range" advice, not fall into the generic
    // "not available" branch just because it also names the LLM.
    if (/stream ended without|temporarily unavailable|timed out/i.test(raw)) {
      return (
        'Profit analysis did not finish in time. Try a shorter date range, or ask again in a moment. ' +
        'If this keeps happening, contact your ZeeFleet administrator.'
      );
    }
    if (/\bllm\b|language model|OPENAI_API_KEY|api key/i.test(raw)) {
      return (
        'Profit analysis is not available at the moment. ' +
        'Please try again later, or contact your ZeeFleet administrator.'
      );
    }
    if (/profit agent/i.test(raw) || opts.isProfitQuestion) {
      return (
        'We could not complete the profit analysis right now. ' +
        'Please try again shortly, or contact your ZeeFleet administrator if this continues.'
      );
    }
  }

  if (/503|llm.*not wired|OPS_AGENT_ENABLED/i.test(raw)) {
    return (
      'ZOE is not available right now. Please try again later, or contact your ZeeFleet administrator.'
    );
  }

  if (/timed out|timeout/i.test(raw)) {
    return (
      'The investigation timed out. Try a more specific question (order, vehicle, or customer), ' +
      'or ask again in a moment.'
    );
  }

  return GENERIC_INVESTIGATION_ERROR;
}

export function isLikelyProfitQuestion(question: string): boolean {
  // Config assumptions / standing cost policy are Operations (get_fleet_config),
  // not Profit Agent — avoid routing those questions into profit-specific errors.
  if (
    /\bstanding\s+costs?\b/i.test(question) &&
    /\b(policy|assumption|assumptions|£\s*\/\s*day|per\s+day|using|assume)\b/i.test(question)
  ) {
    return false;
  }
  if (
    /\b(assumption|assumptions)\b/i.test(question) &&
    /\b(fuel|driver|standing|profitability|insurance|parking)\b/i.test(question)
  ) {
    return false;
  }
  return (
    /\b(margin|margins|profit|profitability|profitable|unprofitable|monday brief|customer.*margin|cost per mile)\b/i.test(
      question,
    ) || /\b(made|make|makes|lost|lose|loses)\b.*\bmoney\b/i.test(question)
  );
}
